import { Injectable } from "@angular/core";
import { TaskRemoteControllerService } from "core/api";
import { LBatchSigning } from "core/config";
import { ITaskDto } from "core/dto";
import { RemoteCollectionService, RemoteCollectionStore } from "core/remotes";
import { LoaderService } from "core/services";
import { Subject, combineLatest, filter, map, take, takeUntil } from "rxjs";
import { DsgDeepTranslationKey } from "../../../pipes/translate.pipe";
import { ITask } from "../dto-map/i-task";
import { SignProcessWizardService } from "../sign-process-wizard.service";
import applyEntityLoader from "./utils/apply-entity-loader";

const taskEntityLoader = applyEntityLoader('task');

const initRemoteStore = (processId: string, taskRemoteControllerService: TaskRemoteControllerService, loaderService: LoaderService<DsgDeepTranslationKey>) =>
  new RemoteCollectionStore<ITaskDto, string>({
    keyField: 'id',
    byKey: (id) => taskEntityLoader({
      obs: taskRemoteControllerService.getOne(id),
      methodKey: 'by-key',
      loaderService
    }),
    load: () => taskEntityLoader({
      obs: taskRemoteControllerService.getByProcess(processId),
      methodKey: 'load',
      loaderService
    }),
    insert: (insertDto) => taskEntityLoader({
      obs: taskRemoteControllerService.create(insertDto).pipe(map(({ id }) => id)),
      methodKey: 'insert',
      loaderService,
      suffix: insertDto.activityId
    }),
    insertBatch: (insertDtoArray) => taskEntityLoader({
      obs: taskRemoteControllerService.insertBatch(insertDtoArray),
      methodKey: 'insert',
      loaderService
    }),
    remove: (id) => taskEntityLoader({
      obs: taskRemoteControllerService.remove(id),
      methodKey: 'remove',
      loaderService
    }),
    update: (id, updateDto) => taskEntityLoader({
      obs: taskRemoteControllerService.update(id, updateDto),
      methodKey: 'update',
      loaderService
    })
  });


@Injectable()
export class RemoteTaskService extends RemoteCollectionService<ITaskDto, ITask> {

  private readonly _destroy = new Subject<void>();

  readonly processIdSubject = this._signProcessWizardService.processIdSubject;
  get processId() {
    return this.processIdSubject.value;
  }

  readonly tasksSubject = this.localCollectionStore.stateSubject;



  constructor(
    private readonly _signProcessWizardService: SignProcessWizardService,
    private readonly _taskRemoteControllerService: TaskRemoteControllerService,
    private readonly _loaderService: LoaderService<DsgDeepTranslationKey>
  ) {

    super();
    const procesUuid = _signProcessWizardService.processIdSubject.value;

    const taskRemoteStore = initRemoteStore(procesUuid || '', _taskRemoteControllerService, _loaderService);

    super.setConfig({
      remoteStore: taskRemoteStore,
      map: (taskDto) => {
        const type = taskDto.taskType;
        const task = structuredClone(taskDto) as unknown as ITask;
        delete (task as any).taskType

        task.type = type;
        return task;
      },
      unMap: (task) => {
        const taskType = task.type;
        const taskDto = structuredClone(task) as unknown as ITaskDto;


        delete (taskDto as any).type
        taskDto.taskType = taskType;
        return taskDto;
      },
      useLocalStore: true
    })

    this.processIdSubject
      .pipe(takeUntil(this._destroy))
      .subscribe((processId) => {
        super.remoteStore = initRemoteStore(processId || '', this._taskRemoteControllerService, this._loaderService)
      })

    this.processIdSubject
      .pipe(
        filter(Boolean),
        takeUntil(this._destroy)
      )
      .subscribe(() => {
        this.load({ autoSync: true }).pipe(take(1)).subscribe();
      })


    this.tasksSubject
      .pipe(takeUntil(this._destroy))
      .subscribe((tasks) => {
        this._signProcessWizardService.tasksSubject.next(tasks)
      })


  }


  deleteActivityTasks(activityId: string) {

    const tasks = this.tasksSubject.value;
    const newTasks = tasks.filter((task) => task.activityId !== activityId);
    this.tasksSubject.next(newTasks);
  }


  #batchNameDict: Record<LBatchSigning, string> = {
    all: '*',
    custom: undefined,
    none: ''
  };

  updateBatchSigningByActivityId(activityId: string, batchSigning: LBatchSigning) {


    const batchName = this.#batchNameDict[batchSigning];
    if (batchName === undefined) {
      return
    }

    const tasks = this.tasksSubject.value;

    const tasksToUpdate = tasks
      .filter(task => task.activityId === activityId)
      .filter((task) => task.batchName !== batchName);


    if (!tasksToUpdate.length) {
      return;
    }

    combineLatest(
      tasksToUpdate.map((task) => this.update(task.id, { ...task, batchName }, 'reloadChanged'))
    )
      .pipe(
        take(1)
      )
      .subscribe()

  }

  ngOnDetroy() {

    this._destroy.next();
    this._destroy.complete();
  }
}

